import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { apiUrl } from '@/api/axios';
import API_ENDPOINTS from '@/api/apiConfig';
import createFormSlice from '@/store/reducers/createFormSlice';
import { handleResponseError } from '@/core/utils/handleResponseError';
import localizePath from '@/core/utils/localizePath';
import { getTranslation } from '@/core/utils/i18nHelper';
import { toAccountSettingsPayload } from '@/store/types/settingsFormTypes';
import type { AccountFormValues } from '@/components/features/settings/types';
import type { AppDispatch } from '@/store/store';

interface AccountSettingsResponse {
    email: string;
    email_verified: boolean;
}

const initialAccountData: AccountFormValues = {
	email: '',
	password: '',
	passwordConfirm: '',
};

// Стартуем с `isFetching: false` — форма должна быть сразу кликабельной.
const formSlice = createFormSlice('accountSettings', initialAccountData, { isFetching: false });

const combinedSlice = createSlice({
	name: 'accountSettings',
	initialState: {
		...formSlice.getInitialState(),
		isDeleted: false,
		emailVerified: null as boolean | null,
	},
	reducers: {
		setAccountSettingsData(state, action) {
			state.data = { ...state.data, ...action.payload };
		},
		setEmailVerified(state, action: PayloadAction<boolean>) {
			state.emailVerified = action.payload;
		},
		accountDeleted(state, action: PayloadAction<string>) {
			state.isDeleted = true;
			state.successMessage = action.payload;
			state.error = null;
			state.isFetching = false;
		},
		...formSlice.caseReducers,
	},
});

export const {
	resetSuccessMessage,
	setError,
	setAccountSettingsData,
	setEmailVerified,
	accountDeleted,
} = combinedSlice.actions;

/**
 * Загружает текущий email пользователя через GET /api/account/settings.
 */
export const loadAccountSettings = () => async (dispatch: AppDispatch) => {
	try {
		const { data } = await apiUrl.get<AccountSettingsResponse>(API_ENDPOINTS.accountSettings);
		dispatch(setAccountSettingsData({ email: data.email }));
		dispatch(setEmailVerified(data.email_verified));
	} catch {
		// Non-critical: form renders with empty default, user can still type their email.
	}
};

/**
 * Сохранение аккаунта (email + опциональный пароль) через PATCH /api/account/settings.
 * При смене email бэкенд отправляет верификационное письмо на новый адрес.
 */
export const saveAccountSettings = (values: AccountFormValues) => async (dispatch: AppDispatch) => {
	const successMessage = getTranslation('settings.account.success_message') as string;

	try {
		const { data } = await apiUrl.patch<AccountSettingsResponse>(
			API_ENDPOINTS.accountSettings,
			toAccountSettingsPayload(values),
			{ headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } },
		);
		dispatch(combinedSlice.actions.formSent(successMessage));
		dispatch(setEmailVerified(data.email_verified));
	} catch (error) {
		handleResponseError(error, (errorMessage: string) => dispatch(combinedSlice.actions.setError(errorMessage)));
	}
};

/**
 * Повторная отправка письма подтверждения.
 *
 * Возвращает признак успеха: плашка в шапке профиля
 * (`EmailVerificationBadge`) живёт вне формы настроек и показывает результат
 * сама, локальным состоянием, а не через `successMessage`/`error` слайса.
 */
export const resendVerification = () => async (dispatch: AppDispatch): Promise<boolean> => {
	try {
		await apiUrl.post(API_ENDPOINTS.accountResendVerification);
		const successMessage = getTranslation('settings.account.verification_sent') as string;
		dispatch(combinedSlice.actions.formSent(successMessage));

		return true;
	} catch (error) {
		handleResponseError(error, (errorMessage: string) => dispatch(combinedSlice.actions.setError(errorMessage)));

		return false;
	}
};

/**
 * Полное удаление аккаунта через DELETE /api/account.
 * После успеха перенаправляет на главную страницу.
 */
export const deleteAccount = () => async (dispatch: AppDispatch) => {
	try {
		await apiUrl.delete(API_ENDPOINTS.accountDelete);
		window.location.href = localizePath('/');
	} catch (error) {
		handleResponseError(error, (errorMessage: string) => dispatch(combinedSlice.actions.setError(errorMessage)));
	}
};

export default combinedSlice.reducer;
export { combinedSlice as accountSettingsSlice };
